import React, { useEffect, useRef, useContext, useCallback } from 'react'
import { useRouteMatch } from 'react-router-dom'

import { dataIdFromObject } from '@sketch/graphql-cache'
import {
  useForDesktop,
  handleFetchMore,
  LoadPageSeparator,
} from '@sketch/components'
import { noop } from '@sketch/utils'

import CommentNewSeparator from '../CommentNewSeparator'
import AnnotationsSkeleton from '../AnnotationsSkeleton'
import CommentSkeleton from '../CommentSkeleton'
import AnnotationCommentBox from '../AnnotationCommentBox'
import { CommentEditingProvider } from '../CommentEditContext'
import CommentOptions from '../CommentOptions'
import AnnotationPopoverResolutionFooter from '../AnnotationPopoverResolutionFooter'

import { DraftCommentContext } from '../DraftCommentContext'
import useGetAnnotationComments from '../../operations/useGetAnnotationComments'
import useCreateAnnotationComment from '../../operations/useCreateAnnotationComment'
import useMarkCommentAsRead from '../../operations/useMarkCommentAsRead'
import useMarkCommentsAsReadList, {
  createCommentProps,
} from '../../hooks/useMarkCommentsAsReadList'

import { isCommentNew, isAnnotationCommentRead } from '../../utils'
import { useActiveAnnotation } from '../../hooks'
import { routes, usePreviewPwd } from '../../../routes'

import {
  AnnotationComment,
  AnnotationCommentsWrapper,
  AnnotationFirstCommentSeparator,
  ViewLatestLink,
  ViewLatestMessage,
} from './AnnotationCommentsList.styles'

export type CommentType = 'FLOATING' | 'FIXED'

interface AnnotationCommentsListProps {
  annotationIdentifier: string
  isViewingLatestVersion: boolean
  header?: React.ReactNode
  cacheOnly?: boolean
  commentType: CommentType
  /**
   * URL of the latest version of the document. Forwarded to
   * `AnnotationCommentBox` to render the "you're viewing an older version"
   * warning banner. Required when `isViewingLatestVersion` is false; ignored
   * otherwise.
   */
  latestVersionUrl?: string
  /**
   * When set, the list is fetched *centred* on this comment: the backend
   * returns a fixed window of `CENTERED_PAGE_SIZE` comments around it (via the
   * `related` argument) instead of the full thread. In this mode pagination is
   * disabled (no "load more") and the list scrolls to bring this comment into
   * view. Used by the inbox preview to focus the notification's comment.
   *
   * When there are newer comments beyond the window, a "see the most recent
   * comment" link is shown that navigates to the annotation on its subject
   * (where the full, live thread lives).
   */
  centeredCommentIdentifier?: string
}

const ENTRIES_PATH = ['annotation', 'comments', 'entries']
const LOADING_PAGE_SIZE = 6

/** Size of the fixed window fetched when centring on a comment. */
const CENTERED_PAGE_SIZE = 5

/**
 * __AnnotationCommentsList__
 *
 * This component displays an incrementally-loaded list of annotation comments.
 *
 * These can be rendered in one of two ways: in a popover as part of a
 * *floating* annotation, or as *fixed* annotations in the sidebar. Use the
 * `commentType` prop to choose how comments should be rendered.
 */
const AnnotationCommentsList = (props: AnnotationCommentsListProps) => {
  const {
    annotationIdentifier,
    header,
    cacheOnly,
    commentType,
    isViewingLatestVersion,
    latestVersionUrl,
    centeredCommentIdentifier,
  } = props
  const isCentered = !!centeredCommentIdentifier
  const listRef = useRef<HTMLDivElement>(null)
  const firstAnnotationCommentRef = useRef<HTMLDivElement | null>(null)
  const centeredCommentRef = useRef<HTMLDivElement | null>(null)
  const hasScrolledToCenter = useRef(false)

  const isDesktopAndBigger = useForDesktop()
  const shouldAutoFocusInput = isDesktopAndBigger

  const previewRouteMatch = useRouteMatch<{ previewID?: string }>(
    routes.PREVIEW_VIEW.template()
  )
  const previewIdentifier = previewRouteMatch?.params.previewID
  const previewPassword = usePreviewPwd(previewIdentifier || '')

  // Centre the returned page on this comment and cap the window size. Left
  // undefined for the regular full-thread view (so the field keeps its usual
  // cache key and the backend applies its default page size). Shared with
  // `useCreateAnnotationComment` so a new reply is written to the same cache
  // entry the list reads from (Apollo keys `comments` by its arguments).
  const commentsQueryVariables = {
    annotationIdentifier,
    related: centeredCommentIdentifier,
    limit: isCentered ? CENTERED_PAGE_SIZE : undefined,
    previewIdentifier,
    previewPassword,
  }

  const { loading, data, error, fetchMore } = useGetAnnotationComments(
    commentsQueryVariables,
    cacheOnly
  )

  const { getDraftComment, setDraftComment } = useContext(DraftCommentContext)

  const draftComment = getDraftComment(annotationIdentifier)

  const createComment = useCreateAnnotationComment(
    annotationIdentifier,
    commentsQueryVariables
  )
  const observe = useMarkCommentsAsReadList()
  const [activeAnnotation] = useActiveAnnotation()

  const commentIdentifierSeparator = useRef<string | null>(null)

  const scrollToComment = useCallback(() => {
    const firstAnnotationCommentElement = firstAnnotationCommentRef.current

    if (!firstAnnotationCommentElement) return

    // We check if the first annotation comment is visible
    const observer = new IntersectionObserver(
      (entries, observer) => {
        if (entries[0].intersectionRatio <= 0) {
          // If not visible, we can scroll
          listRef.current?.scrollTo({
            top: 0,
          })
        }

        observer.disconnect()
      },
      {
        threshold: 0,
      }
    )

    firstAnnotationCommentElement &&
      observer.observe(firstAnnotationCommentElement)
  }, [])

  useEffect(() => {
    /**
     *  Reset the "commentIdentifierSeparator" if the "annotationIdentifier" changes
     *  meaning it will be a different list
     */
    commentIdentifierSeparator.current = null
  }, [annotationIdentifier])

  useEffect(() => {
    // Re-arm the centre scroll whenever the target (or the annotation) changes.
    hasScrolledToCenter.current = false
  }, [centeredCommentIdentifier, annotationIdentifier])

  useEffect(() => {
    // In centred mode, bring the target comment into the middle of the list
    // once, after the window has rendered. The ref is assigned while mapping
    // the comments below, so by the time this effect runs (loading → false)
    // the element is mounted.
    if (!isCentered || hasScrolledToCenter.current) return

    const element = centeredCommentRef.current
    if (!element) return

    window.requestAnimationFrame(() => {
      element.scrollIntoView({ block: 'center' })
      hasScrolledToCenter.current = true
    })
  }, [isCentered, loading])

  /**
   * Mark the first comment as read, if it isn't,
   * since the first comment influences the "annotation.isRead"
   */
  const markCommentAsRead = useMarkCommentAsRead()
  useEffect(() => {
    if (data?.annotation?.isNew) {
      markCommentAsRead([data.annotation.firstComment.identifier])
    }
  }, [data?.annotation, markCommentAsRead])

  /**
   * Mark replies as read if this is the active annotation when this component unmounts
   */

  const cachedComments = useRef(data?.annotation?.comments.entries || [])
  const isActiveAnnotation = activeAnnotation === data?.annotation?.identifier
  cachedComments.current = data?.annotation?.comments.entries || []

  useEffect(() => {
    return () => {
      if (isActiveAnnotation) {
        const comments = cachedComments.current
        const unreadComments = comments?.filter(comment => !comment.isRead)

        const unreadCommentIdentifiers =
          unreadComments?.map(comment => comment.identifier) || []

        markCommentAsRead(unreadCommentIdentifiers)
      }
    }
  }, [isActiveAnnotation, markCommentAsRead])

  const after = data?.annotation?.comments.meta.after

  if (loading || error || !data) {
    return (
      <>
        {header}
        <AnnotationsSkeleton
          error={error}
          placeholderCount={commentType === 'FLOATING' ? 1 : undefined}
        />
        {!error && (
          <AnnotationCommentBox
            shareIdentifier=""
            onCommentSubmit={noop}
            showOlderVersionWarning={false}
          />
        )}
      </>
    )
  }

  const {
    comments,
    share,
    currentSubject,
    firstComment: firstAnnotationComment,
    resolution,
  } = data.annotation!

  const { entries, meta } = comments
  const [firstComment] = entries
  const hasReplies = meta.totalCount > 1

  // In centred mode a `before` cursor means there are more recent comments
  // beyond the fixed window that we haven't loaded.
  const hasNewerComments = isCentered && !!meta.before

  // Destination for the "see the most recent comment" link: the annotation on
  // its subject in the document, where the full live thread lives. Only pages
  // and artboards resolve to a route.
  let viewLatestTo: string | undefined
  if (hasNewerComments) {
    if (currentSubject.__typename === 'Artboard') {
      viewLatestTo = routes.ANNOTATION_REDIRECT.create({
        shareId: share.identifier,
        annotationId: annotationIdentifier,
        query: {
          subjectType: 'Artboard',
          subjectId: currentSubject.permanentArtboardShortId,
        },
      })
    } else if (currentSubject.__typename === 'Page') {
      viewLatestTo = routes.ANNOTATION_REDIRECT.create({
        shareId: share.identifier,
        annotationId: annotationIdentifier,
        query: {
          subjectType: 'Page',
          subjectId: currentSubject.pageUUID ?? '',
        },
      })
    }
  }

  /** Scroll all the way to the top if the first annotation comment is no read */
  if (
    !isCentered && // Centred mode scrolls to its target instead (see effect above)
    !commentIdentifierSeparator.current && // There is no "Read" reply comments (all new and unread)
    !isAnnotationCommentRead(firstAnnotationComment) // First annotation comment is not read
  ) {
    window.requestAnimationFrame(scrollToComment)
  }

  /* Render the comments list */
  const renderedComments = comments.entries.map(comment => {
    /**
     * Save the comment identifier on the ref
     * so when the comments is marked as read we don't
     * loose the reference to it.
     */
    if (
      !commentIdentifierSeparator.current &&
      isCommentNew(firstComment) &&
      !isCommentNew(comment)
    ) {
      // The ID saved here is the comment ID just before the first unread comment
      // - Read
      // - Read -> This ID
      // - (NEW)
      // - Unread
      // - Unread
      commentIdentifierSeparator.current = comment.identifier
    }

    const isFirstComment =
      firstAnnotationComment.identifier === comment.identifier

    const showOptions =
      (comment.userIsCreator || comment.userCanDelete) &&
      !(commentType === 'FLOATING' && isFirstComment)

    return (
      <React.Fragment key={comment.identifier}>
        <AnnotationComment
          ref={ref => {
            if (ref) {
              // Add an Observe to each comment item so we can mark them as read when they are visible
              observe(ref)

              // Save the first annotation comment element in a ref so we can scroll to it if needed
              if (isFirstComment) {
                firstAnnotationCommentRef.current = ref
              }

              // Save the centred comment element so the effect above can scroll
              // it into view.
              if (comment.identifier === centeredCommentIdentifier) {
                centeredCommentRef.current = ref
              }
            }
          }}
          data-first-comment={isFirstComment}
          // Tint the comment we've centred on so it stands out on open; the
          // Comment component dismisses the tint once the user interacts.
          highlight={
            isCentered && comment.identifier === centeredCommentIdentifier
          }
          showPressedState={commentType !== 'FLOATING'}
          body={comment.body}
          user={comment.user}
          identifier={comment.identifier}
          shareIdentifier={share.identifier}
          createdAt={comment.createdAt!}
          edited={comment.isEdited}
          optionsMenu={className =>
            showOptions ? (
              <CommentOptions
                className={className}
                annotationIdentifier={annotationIdentifier}
                commentIdentifier={comment.identifier}
                isFirstComment={isFirstComment}
                hasReplies={hasReplies}
                isAnnotationResolved={!!resolution}
              />
            ) : null
          }
          {...createCommentProps(comment.identifier, !comment.isRead)}
        />

        {/* Render the annotation list if it hasn't been rendered already */}
        {commentIdentifierSeparator.current === comment.identifier &&
          hasReplies && (
            <CommentNewSeparator shouldScrollOnMount={!listRef.current} />
          )}

        {isFirstComment && hasReplies && <AnnotationFirstCommentSeparator />}
      </React.Fragment>
    )
  })

  const handleCommentSubmit = (body: string) => {
    setDraftComment({
      parentAnnotationId: annotationIdentifier,
      commentValue: '',
    })

    // Create the comment
    createComment({
      variables: {
        identifier: annotationIdentifier,
        body,
        previewIdentifier,
        previewPassword,
      },
    })

    // Scroll to the end
    setTimeout(() => {
      listRef.current?.scrollTo({
        top: listRef.current.scrollHeight,
        behavior: 'smooth',
      })
    }, 0)
  }

  return (
    <CommentEditingProvider>
      <AnnotationCommentsWrapper ref={listRef}>
        {/**
         * The header is included inside the list rendering
         * so we take advance of position sticky and show a
         * border bellow the header when there's more items
         * to load
         */}
        {header}

        {/**
         * The CommentSkeleton renders the annotation placeholders. In centred
         * mode the window is fixed, so there are no further pages to hint at.
         */}
        <CommentSkeleton
          count={
            isCentered
              ? 0
              : Math.min(LOADING_PAGE_SIZE, meta.totalCount - entries.length)
          }
        />

        {/* Centred mode shows a fixed window — no "load more" pagination. */}
        {!isCentered && after && (
          <LoadPageSeparator
            key={after}
            loadNewPage={handleFetchMore(fetchMore, ENTRIES_PATH, {
              dataIdFromObject,
              after,
            })}
          />
        )}

        {[...renderedComments].reverse()}

        {/* The window is centred on an older comment and there are more recent
         * ones we haven't loaded — link to the annotation on its subject, where
         * the full, live thread lives. */}
        {viewLatestTo && (
          <ViewLatestMessage>
            To see the most recent comment click{' '}
            <ViewLatestLink to={viewLatestTo}>here</ViewLatestLink>
          </ViewLatestMessage>
        )}
      </AnnotationCommentsWrapper>
      {resolution ? (
        <AnnotationPopoverResolutionFooter
          user={resolution.user}
          resolvedDate={resolution.resolvedAt!}
        />
      ) : (
        <AnnotationCommentBox
          shareIdentifier={share.identifier}
          showOlderVersionWarning={!isViewingLatestVersion}
          latestVersionUrl={latestVersionUrl}
          comment={draftComment?.commentValue}
          onCommentChange={(commentValue: string) => {
            setDraftComment({
              parentAnnotationId: annotationIdentifier,
              commentValue,
            })
          }}
          onCommentSubmit={handleCommentSubmit}
          isReply
          autoFocus={shouldAutoFocusInput}
        />
      )}
    </CommentEditingProvider>
  )
}

export default AnnotationCommentsList
